/**
 * Unified Pulse Loader
 * Uses the TrainerPulse brand's ECG/pulse line animation
 */

import { cn } from '@/lib/utils';

interface PulseLoaderProps {
  /** Page name to display (e.g., "Dashboard", "Clients"). Shows "Loading {pageName}..." */
  pageName?: string;
  /** Custom message to display instead of default */
  message?: string;
  /** Whether to show fullscreen or inline */
  fullScreen?: boolean;
  /** Additional className for the container */
  className?: string;
}

/**
 * Animated pulse line SVG that mimics an ECG/heartbeat monitor
 */
function PulseLine({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 40"
      className={cn("w-32 h-12", className)}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background line (faded) */}
      <path
        d="M0 20 L20 20 L25 20 L30 8 L35 32 L40 12 L45 28 L50 20 L55 20 L100 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/20"
      />
      {/* Animated pulse line */}
      <path
        d="M0 20 L20 20 L25 20 L30 8 L35 32 L40 12 L45 28 L50 20 L55 20 L100 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="200"
        className="text-primary animate-pulse-draw"
      />
      {/* Traveling dot */}
      <circle
        r="4"
        fill="currentColor"
        className="text-primary"
      >
        <animateMotion
          dur="1.5s"
          repeatCount="indefinite"
          path="M0 20 L20 20 L25 20 L30 8 L35 32 L40 12 L45 28 L50 20 L55 20 L100 20"
        />
      </circle>
    </svg>
  );
}

/**
 * Unified loading component for the entire app.
 * Use this instead of custom spinners for consistency.
 *
 * @example
 * // Fullscreen loader (for route guards, initial load)
 * <PulseLoader fullScreen pageName="Dashboard" />
 *
 * @example
 * // Inline loader (for components, data fetching)
 * <PulseLoader pageName="clients" />
 *
 * @example
 * // Custom message
 * <PulseLoader message="Saving changes..." />
 */
export function PulseLoader({
  pageName,
  message,
  fullScreen = false,
  className,
}: PulseLoaderProps) {
  const displayMessage = message
    ? message
    : pageName
      ? `Loading ${pageName}...`
      : 'Loading...';

  const content = (
    <div className={cn(
      "flex flex-col items-center justify-center gap-4",
      fullScreen ? "min-h-screen bg-background" : "py-12",
      className
    )}>
      <div className="relative">
        <PulseLine />
      </div>
      <p className="font-sans text-muted-foreground text-xs font-medium animate-pulse">
        {displayMessage}
      </p>
    </div>
  );

  return content;
}

/**
 * Fullscreen variant for convenience
 */
export function FullScreenLoader({ pageName, message }: Pick<PulseLoaderProps, 'pageName' | 'message'>) {
  return <PulseLoader fullScreen pageName={pageName} message={message} />;
}
